// Space storm event: ~25 s of fog of war, thunder flashes, and firebolt
// strikes. Fog is render-side (a dark overlay with a limited vision radius
// around the player); the sim records thunder flash ticks and resolves
// firebolt strikes — area damage on enemies, with the occasional bolt aimed
// at the player. Deterministic: scheduling and targeting rolls come from
// private mulberry32 streams derived from the run seed — never Math.random
// or wall-clock (GDD Appendix A §A.1).

import type { WorldState } from './state.js';
import type { Enemy, FireboltStrike, SpaceStormState, Vec2 } from '../types.js';
import { cfg } from '../config.js';
import { wrapArena, wrappedDelta } from './collision.js';
import { applyDamageToPlayer } from './combat.js';
import { killEnemy } from './kill.js';
import { RngStream } from './rng.js';
import type { GameEventDef } from './events-manager.js';

/** Storm duration: ~25 s at 60 Hz. */
export const SPACE_STORM_DURATION_TICKS = 25 * 60;
/** Thunder flash every 3–5 s at 60 Hz. */
export const THUNDER_MIN_TICKS = 3 * 60;
export const THUNDER_MAX_TICKS = 5 * 60;
/** Firebolt strike every 2–4 s at 60 Hz. */
export const FIREBOLT_MIN_TICKS = 2 * 60;
export const FIREBOLT_MAX_TICKS = 4 * 60;
/** Firebolt area-damage radius (arena units). */
export const FIREBOLT_RADIUS = 3;
/** Flat firebolt damage per strike (enemies and player alike). */
export const FIREBOLT_DAMAGE = 30;
/** Chance each firebolt targets the player instead of an enemy. */
export const FIREBOLT_PLAYER_CHANCE = 0.15;
/** Vision radius the fog of war leaves clear around the player (arena units). */
export const SPACE_STORM_VISION_RADIUS = 12;
/** Strikes older than this are pruned (covers the renderer's fade window). */
export const STRIKE_LINGER_TICKS = 36;

// Private stream salts: scheduling and targeting stay independent of the
// shared world streams and of each other (wormhole/bonus salt pattern), so
// spawn/loot/ai determinism is unaffected by the storm.
const STORM_SCHEDULE_SALT = 0x3c6ef372;
const STORM_TARGET_SALT = 0xa54ff53a;

function createSpaceStormState(): SpaceStormState {
  return {
    active: false,
    start_tick: 0,
    duration_ticks: SPACE_STORM_DURATION_TICKS,
    next_thunder_tick: 0,
    last_thunder_tick: -1000,
    next_firebolt_tick: 0,
    strikes: []
  };
}

/** Render-facing snapshot; game.ts pushes this to the renderer each frame. */
export interface SpaceStormRenderState {
  active: boolean;
  visionRadius: number;
  lastThunderTick: number;
  strikes: readonly FireboltStrike[];
}

/**
 * The space storm run event. Owns its state and private RNG streams; mirrors
 * the BonusGame lifecycle — constructed once, reset() after every
 * world.reset(seed).
 */
export class SpaceStormEvent implements GameEventDef {
  readonly id = 'space-storm';
  readonly name = 'SPACE STORM';
  readonly duration = SPACE_STORM_DURATION_TICKS;
  private readonly state: SpaceStormState = createSpaceStormState();
  private scheduleStream: RngStream;
  private targetStream: RngStream;

  constructor(seed = 0) {
    this.scheduleStream = new RngStream((seed ^ STORM_SCHEDULE_SALT) >>> 0);
    this.targetStream = new RngStream((seed ^ STORM_TARGET_SALT) >>> 0);
  }

  /** Reseed both streams from the run seed; call after world.reset(seed). */
  reset(seed: number): void {
    this.scheduleStream = new RngStream((seed ^ STORM_SCHEDULE_SALT) >>> 0);
    this.targetStream = new RngStream((seed ^ STORM_TARGET_SALT) >>> 0);
    const fresh = createSpaceStormState();
    const s = this.state;
    s.active = fresh.active;
    s.start_tick = fresh.start_tick;
    s.duration_ticks = fresh.duration_ticks;
    s.next_thunder_tick = fresh.next_thunder_tick;
    s.last_thunder_tick = fresh.last_thunder_tick;
    s.next_firebolt_tick = fresh.next_firebolt_tick;
    s.strikes.length = 0;
  }

  get active(): boolean {
    return this.state.active;
  }

  /** Current state snapshot for the renderer (fog, thunder, firebolts). */
  getRenderState(): SpaceStormRenderState {
    return {
      active: this.state.active,
      visionRadius: SPACE_STORM_VISION_RADIUS,
      lastThunderTick: this.state.last_thunder_tick,
      strikes: this.state.strikes
    };
  }

  start(world: WorldState, tick: number): void {
    const s = this.state;
    s.active = true;
    s.start_tick = tick;
    s.strikes.length = 0;
    s.last_thunder_tick = -1000;
    s.next_thunder_tick = tick + rollRange(this.scheduleStream, THUNDER_MIN_TICKS, THUNDER_MAX_TICKS);
    s.next_firebolt_tick = tick + rollRange(this.scheduleStream, FIREBOLT_MIN_TICKS, FIREBOLT_MAX_TICKS);
    world.events.emit('phase_change', { system: 'space-storm', phase: 'start' }, tick);
  }

  update(world: WorldState, tick: number, _progress: number): void {
    const s = this.state;
    if (!s.active) return;

    // Thunder: a bright full-screen flash every 3–5 s. The flash itself is
    // render-side; the sim only records the tick so replays stay identical.
    while (tick >= s.next_thunder_tick) {
      s.last_thunder_tick = s.next_thunder_tick;
      s.next_thunder_tick += rollRange(this.scheduleStream, THUNDER_MIN_TICKS, THUNDER_MAX_TICKS);
    }

    // Firebolts: lightning strikes on seeded targets every 2–4 s. With the
    // 1-tick sim step, next_firebolt_tick === tick on the crossing tick.
    while (tick >= s.next_firebolt_tick) {
      this.strike(world, s.next_firebolt_tick);
      s.next_firebolt_tick += rollRange(this.scheduleStream, FIREBOLT_MIN_TICKS, FIREBOLT_MAX_TICKS);
    }

    // Prune strikes whose visual has fully faded.
    let write = 0;
    for (let i = 0; i < s.strikes.length; i++) {
      if (tick - s.strikes[i].tick <= STRIKE_LINGER_TICKS) {
        s.strikes[write++] = s.strikes[i];
      }
    }
    s.strikes.length = write;
  }

  end(world: WorldState, tick: number): void {
    this.state.active = false;
    world.events.emit('phase_change', { system: 'space-storm', phase: 'end' }, tick);
  }

  /**
   * Resolve one firebolt strike: pick a seeded target (an enemy position, or
   * occasionally the player), apply area damage to every enemy in the blast,
   * and record the strike so the renderer can animate the bolt.
   */
  private strike(world: WorldState, tick: number): void {
    const player = world.player;
    // The roll is always consumed so stream usage stays independent of
    // gameplay state.
    const targetPlayer = player.alive && this.targetStream.next() < FIREBOLT_PLAYER_CHANCE;

    let pos: Vec2;
    if (targetPlayer) {
      pos = { x: player.pos.x, y: player.pos.y };
    } else {
      const enemy = this.pickEnemy(world);
      if (enemy) {
        pos = { x: enemy.pos.x, y: enemy.pos.y };
      } else {
        // No live enemies: strike a seeded point near the player so the storm
        // still reads visually. Damages nothing the player isn't aiming at.
        const angle = this.targetStream.next_range(0, Math.PI * 2);
        const dist = this.targetStream.next_range(6, 18);
        pos = { x: player.pos.x + Math.cos(angle) * dist, y: player.pos.y + Math.sin(angle) * dist };
        wrapArena(pos);
      }
    }

    // Area damage: every targetable enemy inside the blast (toroidal).
    const victims: Enemy[] = [];
    for (const e of world.enemies.alive()) {
      if (!e.targetable) continue;
      const d = wrappedDelta(e.pos, pos);
      const r = FIREBOLT_RADIUS + e.hitbox_radius;
      if (d.x * d.x + d.y * d.y <= r * r) victims.push(e);
    }
    for (const e of victims) {
      e.hp -= FIREBOLT_DAMAGE;
      world.events.emit(
        'hit',
        {
          target_id: e.id,
          damage: FIREBOLT_DAMAGE,
          crit: false,
          weapon_id: this.id,
          pos: { x: e.pos.x, y: e.pos.y }
        },
        tick
      );
      if (e.hp <= 0) {
        // Shared death handling (spec-arsenal): death event, def drops,
        // health roll, splitter minis — same as weapon kills.
        killEnemy(world, e, tick);
      }
    }

    // Player-targeted bolts damage the player through the standard
    // shield/HP pipeline, gated by dash i-frames and the mercy window.
    if (targetPlayer && player.alive) {
      const d = wrappedDelta(player.pos, pos);
      const r = FIREBOLT_RADIUS + player.hitbox_radius;
      if (d.x * d.x + d.y * d.y <= r * r) {
        if (player.dash.iframes_until <= tick && player.mercy_until <= tick) {
          applyDamageToPlayer(player, FIREBOLT_DAMAGE, tick);
          player.mercy_until = tick + Math.round(cfg.getNumber('player.mercy_window') * 60);
          world.events.emit(
            'player_hit',
            { damage: FIREBOLT_DAMAGE, source: this.id, pos: { x: player.pos.x, y: player.pos.y } },
            tick
          );
        }
      }
    }

    this.state.strikes.push({ pos, tick, radius: FIREBOLT_RADIUS, targeted_player: targetPlayer });
  }

  /** Random alive, targetable enemy; exactly one targeting-stream roll. */
  private pickEnemy(world: WorldState): Enemy | null {
    let count = 0;
    for (const e of world.enemies.alive()) {
      if (e.targetable) count++;
    }
    if (count === 0) return null;
    let index = this.targetStream.next_int(count);
    for (const e of world.enemies.alive()) {
      if (!e.targetable) continue;
      if (index-- === 0) return e;
    }
    return null;
  }
}

/** Seeded integer tick count in [minTicks, maxTicks]. */
function rollRange(stream: RngStream, minTicks: number, maxTicks: number): number {
  return Math.floor(stream.next_range(minTicks, maxTicks + 1));
}
